import { Config, Context, Effect, Layer, Redacted, Runtime, pipe } from "effect";
import {
  ChainId,
  ChainKey,
  PublicClient,
  chains,
  getChainKeyFromProtocol,
} from "./chain";
import { ProtocolKey } from "./protocol";
import { FallbackTransport, createPublicClient, fallback, http } from "viem";
import type { ConfigError } from "effect/ConfigError";
import {
  RpcMetricsService,
  ScriptRpcMetricsServiceLive,
  updateRpcMetrics,
} from "./rpc-metrics";

export interface RpcConfig {
  readonly DRPC_KEY: Redacted.Redacted<string>;
  readonly INFURA_KEY: Redacted.Redacted<string>;
  readonly ALCHEMY_KEY: Redacted.Redacted<string>;
}

export class RpcConfigService extends Context.Tag("RpcConfigService")<
  RpcConfigService,
  RpcConfig
>() {}

export const ServerRpcConfig = Layer.effect(
  RpcConfigService,
  Effect.gen(function* () {
    const DRPC_KEY = yield* Config.redacted("DRPC_KEY");
    const INFURA_KEY = yield* Config.redacted("INFURA_KEY");
    const ALCHEMY_KEY = yield* Config.redacted("ALCHEMY_KEY");

    return {
      DRPC_KEY,
      INFURA_KEY,
      ALCHEMY_KEY,
    };
  })
);

export interface PublicClientServiceInterface {
  readonly getPublicClientByChain: (chainKey: ChainKey) => PublicClient;
  readonly getPublicClient: (protocolKey: ProtocolKey) => PublicClient;
}

export class PublicClientService extends Context.Tag("PublicClientService")<
  PublicClientService,
  PublicClientServiceInterface
>() {}

interface PublicClientServiceParams {
  /**
   * Wait (ms) between viem RPC batches. Used by jobs to throttle the RPC
   * endpoint; 0 (the default) preserves the original eager batching.
   */
  wait?: number;
}

export function PublicClientServiceFactory({
  wait = 0,
}: PublicClientServiceParams = {}): Layer.Layer<
  PublicClientService,
  never,
  RpcConfigService | RpcMetricsService
> {
  return Layer.effect(
    PublicClientService,
    Effect.gen(function* () {
      const rpcConfig = yield* RpcConfigService;
      const rpcMetricsRef = yield* RpcMetricsService;

      /**
       * Using this setup to run an effect each time the onFetchRequest callback is invoked.
       *
       * Approach was taken from here: https://effect.website/docs/integrations/express
       */
      const runFork = Runtime.runFork(yield* Effect.runtime<never>());

      const transports = createTransports({
        rpcConfig,
        onFetchRequest: (request) => {
          runFork(updateRpcMetrics(request, rpcMetricsRef));
        },
        wait,
      });

      function getPublicClientByChain(chainKey: ChainKey): PublicClient {
        const chain = chains[chainKey];
        const transport = transports[chain.id];

        return createPublicClient({
          chain,
          transport,
          batch: { multicall: true },
        });
      }

      function getPublicClient(protocolKey: ProtocolKey): PublicClient {
        return pipe(
          protocolKey,
          getChainKeyFromProtocol,
          getPublicClientByChain
        );
      }

      return {
        getPublicClientByChain,
        getPublicClient,
      };
    })
  );
}

/**
 * Server-side client for scripts and the sandbox. Accumulates RPC metrics in
 * memory and logs them to the console when the scope closes, but never writes
 * to Redis (see ScriptRpcMetricsServiceLive).
 */
export const ScriptPublicClientServiceLive: Layer.Layer<
  PublicClientService | RpcMetricsService,
  ConfigError,
  never
> = pipe(
  PublicClientServiceFactory(),
  Layer.provide(ServerRpcConfig),
  Layer.provideMerge(ScriptRpcMetricsServiceLive)
);

export class ChainPublicClientService extends Context.Tag(
  "ChainPublicClientService"
)<ChainPublicClientService, { publicClient: PublicClient }>() {}

const { ethereum, arbitrum, base, optimism, local, anvil } = chains;

const DRPC_BATCH_SIZE = 32;
const INFURA_BATCH_SIZE = 32;
const ALCHEMY_BATCH_SIZE = 32;

interface CreateTransportsParams {
  rpcConfig: RpcConfig;
  onFetchRequest?: (request: Request) => void;
  wait?: number;
}

/**
 * Sets up the transports needed to configure wagmi or create a viem public client.
 */
export function createTransports({
  rpcConfig,
  onFetchRequest,
  wait = 0,
}: CreateTransportsParams): Record<ChainId, FallbackTransport> {
  const DRPC_KEY = Redacted.value(rpcConfig.DRPC_KEY);
  const INFURA_KEY = Redacted.value(rpcConfig.INFURA_KEY);
  const ALCHEMY_KEY = Redacted.value(rpcConfig.ALCHEMY_KEY);

  return {
    [ethereum.id]: fallback([
      http(`https://lb.drpc.live/ogrpc?network=ethereum&dkey=${DRPC_KEY}`, {
        batch: {
          batchSize: DRPC_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
    ]),
    [arbitrum.id]: fallback([
      http(`https://lb.drpc.live/ogrpc?network=arbitrum&dkey=${DRPC_KEY}`, {
        batch: {
          batchSize: DRPC_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://arb-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://arbitrum-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
    ]),
    [base.id]: fallback([
      http(`https://lb.drpc.live/ogrpc?network=base&dkey=${DRPC_KEY}`, {
        batch: {
          batchSize: DRPC_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://base-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
    ]),
    [optimism.id]: fallback([
      http(`https://lb.drpc.live/ogrpc?network=optimism&dkey=${DRPC_KEY}`, {
        batch: {
          batchSize: DRPC_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://opt-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
      http(`https://optimism-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
          wait,
        },
        onFetchRequest,
      }),
    ]),
    [local.id]: fallback([http(undefined, { onFetchRequest })]),
    [anvil.id]: fallback([http(undefined, { onFetchRequest })]),
  };
}
